import type { CursorPage, Insight, Topic, TopicDetailResponse } from '@mira/clients-shared';
import { useState } from 'react';
import { createWebApiClient } from '../../api/webSession.js';
import { hrefForRoute, navigateTo } from '../../app/router.js';
import { showToast } from '../../app/toast.js';
import { Avatar, EmptyState, ErrorState, IconGlyph, SegmentedTabs } from '../../ui/primitives/index.js';
import { DangerDialog, DetailTopbar, ListState } from '../../ui/patterns/index.js';
import { TopicRow } from '../../ui/domain/index.js';
import { useApiQuery } from '../api-state.js';
import { formatDateLabel, page } from '../shared.js';
import { sampleInsights, sampleTopics } from '../reference-preview-data.js';

function emptyTopic(topicId: string): Topic {
  return { id: topicId, title: 'Topics', summary: null };
}

export function TopicsScreen(props: { preview?: boolean; screenId?: string }) {
  const [search, setSearch] = useState(props.screenId === 'ls_01_topics_search_results' ? 'communication' : props.screenId === 'ls_02_topics_filter_no_results' ? 'no results' : '');
  const [filter, setFilter] = useState('all');
  const previewState = topicsPreviewState(props.screenId);
  const topics = useApiQuery<CursorPage<Topic>>({
    preview: Boolean(props.preview),
    previewData: page(sampleTopics),
    previewError: 'Topics could not be loaded right now. Please try again.',
    previewState,
    load: () => createWebApiClient().topics({ limit: 30 }),
    isEmpty: (value) => value.items.length === 0,
    deps: [],
  });
  const visibleTopics = topics.data.items.filter((topic) => {
    const query = search.trim().toLowerCase();
    const matchesQuery = !query || `${topic.title} ${topic.summary || ''} ${topic.category_label || ''}`.toLowerCase().includes(query);
    const matchesFilter = filter === 'all' || topic.category_label === filter;
    return matchesQuery && matchesFilter;
  });
  const filters = [
    { id: 'all', label: 'All' },
    ...Array.from(new Set(topics.data.items.map((topic) => topic.category_label).filter((value): value is string => Boolean(value))))
      .map((label) => ({ id: label, label })),
  ];

  return (
    <article className="mira-business-screen mira-topics-screen">
      <header className="mira-topics-header">
        <div className="mira-main-brand-row">
          <span className="mira-main-brand">Mira</span>
          <span className="mira-header-round-icon" aria-hidden="true">…</span>
        </div>
        <h1>Topics</h1>
        <p>Threads that keep resurfacing in conversation, gathered here for continued understanding.</p>
      </header>
      <label className="mira-topic-search-wrap">
        <span aria-hidden="true"><IconGlyph value="⌕" /></span>
        <input aria-label="Search topics" onChange={(event) => setSearch(event.currentTarget.value)} placeholder="Search topics" type="search" value={search} />
      </label>
      <SegmentedTabs className="mira-topic-tabs" value={filter} items={filters} onChange={setFilter} />
      {props.screenId === 'td_03_deleted_to_list' ? <p className="mira-soft-note">Topic deleted. The original conversation and saved memories remain under their own rules.</p> : null}
      <ListState
        state={topics.state}
        emptyTitle="No topics yet"
        emptyBody="When a thread takes shape across conversations, Mira gathers it here."
        emptyActionHref={hrefForRoute('today')}
        emptyActionLabel="Back to Today"
        errorTitle={topics.error || 'Topics could not be loaded'}
        errorBody="Please try again later, or return to Today first."
        errorActionHref={hrefForRoute('today')}
        errorActionLabel="Back to Today"
      >
        {visibleTopics.length > 0 ? (
          <div className="mira-topic-visual-list">
            {visibleTopics.map((topic) => (
              <TopicRow href={hrefForRoute('topic-detail', { topicId: topic.id })} key={topic.id} topic={topic} />
            ))}
          </div>
        ) : (
          <p>No topics match. Return to Today to start a new conversation.</p>
        )}
      </ListState>
    </article>
  );
}

export function TopicDetailScreen(props: { preview?: boolean; screenId?: string; showDelete?: boolean; tab?: string | null; topicId: string }) {
  const [actionError, setActionError] = useState(props.screenId === 'td_05_delete_failed' ? 'This topic could not be deleted right now. Please try again.' : '');
  const deleteBusy = props.preview && props.screenId === 'td_02_deleting';
  const detail = useApiQuery<TopicDetailResponse>({
    preview: Boolean(props.preview),
    previewData: { topic: sampleTopics[0], summaries: [{ summary_kind: 'overview', content: sampleTopics[0].summary || '' }] },
    emptyData: { topic: emptyTopic(props.topicId), summaries: [] },
    previewError: 'Topics could not be loaded right now. Please try again.',
    previewState: topicDetailPreviewState(props.screenId),
    load: () => createWebApiClient().topic(props.topicId),
    deps: [props.topicId],
  });
  const insights = useApiQuery<CursorPage<Insight>>({
    preview: Boolean(props.preview),
    previewData: page(sampleInsights),
    emptyData: page([]),
    previewError: 'Insights could not be loaded right now. Please try again.',
    previewState: topicInsightsPreviewState(props.screenId),
    load: () => createWebApiClient().topicInsights(props.topicId, { limit: 30 }),
    isEmpty: (value) => value.items.length === 0,
    deps: [props.topicId],
  });
  const topic = detail.data.topic;
  const value = topicDetailTab(props.tab, Boolean(props.preview));

  async function continueChat() {
    if (props.preview) {
      navigateTo('chat', { conversationId: 'new' }, { source: 'topic-detail' });
      return;
    }
    try {
      setActionError('');
      const response = await createWebApiClient().continueTopic(props.topicId);
      navigateTo('chat', { conversationId: response.conversationId }, {
        source: 'topic-detail',
        returnTo: hrefForRoute('topic-detail', { topicId: props.topicId }).slice(1),
      });
    } catch {
      setActionError('This topic could not be continued right now. Please try again.');
    }
  }

  async function deleteTopic() {
    if (props.preview) return;
    try {
      setActionError('');
      await createWebApiClient().deleteTopic(props.topicId);
      showToast('Topic deleted.');
      navigateTo('topics');
    } catch {
      setActionError('This topic could not be deleted right now. Please try again.');
    }
  }

  return (
    <article className="mira-business-screen mira-topic-detail-screen" data-tab={value} data-sheet={props.showDelete ? 'delete' : undefined}>
      <DetailTopbar
        backHref={hrefForRoute('topics')}
        backLabel="Topics"
        label={value === 'insights' ? 'Topic insights' : 'Mira'}
        menuLabel="Delete topic"
        onMenu={() => navigateTo('topic-detail', { topicId: props.topicId }, { sheet: 'delete', tab: value })}
      />
      <header className="mira-topic-detail-hero">
        <span className="mira-topic-detail-icon" aria-hidden="true"><IconGlyph value={topicDetailIcon(value)} /></span>
        <div>
          <h1>{topic.title}</h1>
          <p>{topicDetailIntro(value)}</p>
        </div>
      </header>
      <nav className="mira-detail-tabs" aria-label="Topic content">
        {topicTabs(Boolean(props.preview)).map((tab) => (
          <a data-active={value === tab.id || undefined} href={topicTabHref(props.topicId, tab.id)} key={tab.id}>{tab.label}</a>
        ))}
      </nav>
      <ListState state={detail.state} errorTitle={detail.error || 'Topics could not be loaded'} errorBody="This topic could not be opened right now. Please try again later." errorActionHref={hrefForRoute('topics')} errorActionLabel="Back to topics">
        <div className="mira-topic-detail-content">
          {value === 'overview' ? <TopicOverviewContent summaries={detail.data.summaries || []} topic={topic} onContinue={continueChat} /> : null}
          {value === 'insights' ? (
          <ListState state={insights.state} emptyTitle="No insights for this topic yet" emptyBody="When a conversation forms a reminder worth revisiting, it shows up here." emptyActionHref={hrefForRoute('topic-detail', { topicId: props.topicId })} emptyActionLabel="View overview" errorTitle={insights.error || 'Insights could not be loaded'} errorBody="Insights for this topic could not be loaded right now." errorActionHref={hrefForRoute('topic-detail', { topicId: props.topicId })} errorActionLabel="View overview">
            <TopicInsightsContent insights={insights.data.items} />
          </ListState>
          ) : null}
          {value === 'steps' ? <TopicNextStepsContent onContinue={continueChat} /> : null}
          {value === 'history' ? <TopicHistoryContent onContinue={continueChat} /> : null}
        </div>
      </ListState>
      {actionError ? <ErrorState title="Action failed" body={actionError} /> : null}
      <button className="mira-subtle-danger" onClick={() => navigateTo('topic-detail', { topicId: props.topicId }, { sheet: 'delete', tab: value })} type="button">Delete topic</button>
      {props.showDelete ? (
        <DangerDialog
          busy={deleteBusy}
          body={`This removes “${topic.title}” from your topic list. It does not delete the original conversation, memories, insights, relationships, or message history.`}
          confirmLabel="Delete"
          confirmLoadingLabel="Deleting…"
          onCancel={() => navigateTo('topic-detail', { topicId: props.topicId })}
          onConfirm={deleteTopic}
          stackActions
          title="Delete this topic?"
        />
      ) : null}
    </article>
  );
}

function topicsPreviewState(screenId?: string): 'loading' | 'empty' | 'error' | 'ready' | undefined {
  if (screenId === 'ls_09_topics_loading') return 'loading';
  if (screenId === 'ls_03_topics_api_error') return 'error';
  if (screenId === 'td_04_deleted_empty') return 'empty';
  return undefined;
}

function topicDetailPreviewState(screenId?: string): 'loading' | 'empty' | 'error' | 'ready' | undefined {
  if (screenId === 'topic_02_detail_loading') return 'loading';
  if (screenId === 'topic_03_detail_error') return 'error';
  return undefined;
}

function topicInsightsPreviewState(screenId?: string): 'loading' | 'empty' | 'error' | 'ready' | undefined {
  if (screenId === 'topic_04_insights_empty') return 'empty';
  if (screenId === 'topic_05_insights_error') return 'error';
  return undefined;
}

type TopicDetailTab = 'overview' | 'insights' | 'steps' | 'history';

const currentTopicTabs: Array<{ id: TopicDetailTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'insights', label: 'Insights' },
];

const archivedTopicTabs: Array<{ id: TopicDetailTab; label: string }> = [
  ...currentTopicTabs,
  { id: 'steps', label: 'Next steps' },
  { id: 'history', label: 'History' },
];

const previewTopicStepCards = [
  ['♡', 'Try a 20-minute check-in', 'Set aside calm, uninterrupted time to ask how the other person is doing-and really listen.', "May help when you've both been busy or distant."],
  ['♧', "Share what's underneath", "Open up about a feeling or need that's been hard to put into words.", 'May help when small things keep piling up.'],
  ['⌁', 'Ask with curiosity', 'Ask about their perspective without trying to fix or defend.', 'May help when you want to understand better.'],
  ['☼', 'Plan a connection moment', 'Do something you both enjoy-just the two of you.', 'May help when routine is getting in the way.'],
  ['▣', 'Reflect and write it out', 'Write down what matters most to you before you talk.', 'May help when emotions feel tangled.'],
];

const previewTopicHistoryRows = [
  ['May 15, 2025', '7:42 PM', 'Talked about feeling disconnected lately. You shared you need more quality time together.', 'Conversation'],
  ['May 10, 2025', '9:18 PM', 'Discussed how interruptions make it difficult to finish your thoughts.', 'Insight'],
  ['May 5, 2025', '8:06 PM', 'Explored what helps both of you feel heard during disagreements.', 'Conversation'],
  ['Apr 28, 2025', '6:31 PM', 'You reflected on reacting quickly and wanting to pause before responding.', 'Insight'],
  ['Apr 22, 2025', '7:55 PM', 'Set an intention to check in daily, even for just a few minutes.', 'Commitment'],
];

function topicTabs(includePostCore: boolean): Array<{ id: TopicDetailTab; label: string }> {
  return includePostCore ? archivedTopicTabs : currentTopicTabs;
}

function topicDetailTab(value?: string | null, includePostCore = false): TopicDetailTab {
  if (value === 'insights') return value;
  if (includePostCore && (value === 'steps' || value === 'history')) return value;
  return 'overview';
}

function topicTabHref(topicId: string, tab: TopicDetailTab): string {
  return hrefForRoute('topic-detail', { topicId }, tab === 'overview' ? {} : { tab });
}

function topicDetailIcon(tab: TopicDetailTab): string {
  if (tab === 'insights') return '♧';
  if (tab === 'steps') return '✧';
  if (tab === 'history') return '◷';
  return '◒';
}

function topicDetailIntro(tab: TopicDetailTab): string {
  if (tab === 'insights') return 'Only insights sourced from this topic.';
  if (tab === 'steps') return 'Keep moving forward from what is already understood.';
  if (tab === 'history') return 'See how this topic took shape.';
  return 'A topic that gradually formed across related conversations.';
}

function TopicOverviewContent(props: { summaries: TopicDetailResponse['summaries']; topic: Topic; onContinue: () => void }) {
  const summaryItems = props.summaries.length > 0
    ? props.summaries
    : props.topic.summary
      ? [{ summary_kind: 'summary', content: props.topic.summary }]
      : [];
  return (
    <>
      <section className="mira-topic-summary-card">
        <header>
          <span aria-hidden="true"><IconGlyph value="✦" /></span>
          <h2>Mira summary</h2>
        </header>
        {summaryItems.length > 0 ? summaryItems.map((summary) => (
          <p key={`${summary.summary_kind}-${summary.updated_at || summary.content}`}>{summary.content}</p>
        )) : <p>No reliable summary has formed for this topic yet.</p>}
        {props.topic.conversation_id ? (
          <a className="mira-topic-relationship-strip" href={hrefForRoute('chat', { conversationId: props.topic.conversation_id }, { source: 'topic-detail' })}>
            <Avatar decorative label={props.topic.title || 'Topic'} />
            <span>Open the conversation behind this topic</span>
            <strong>{props.topic.title}</strong>
            <span className="mira-row-chevron" aria-hidden="true"><IconGlyph value="›" /></span>
          </a>
        ) : null}
      </section>
      <button className="mira-topic-primary-action" onClick={props.onContinue} type="button"><span aria-hidden="true"><IconGlyph value="✎" /></span>Continue this topic</button>
      <p className="mira-topic-private-note"><span aria-hidden="true"><IconGlyph value="♧" /></span>This topic and its conversation belong to your account. No other user can read them.</p>
    </>
  );
}

function TopicInsightsContent(props: { insights: Insight[] }) {
  const cards = props.insights.slice(0, 4);
  return (
    <div className="mira-topic-card-list">
      {cards.map((insight, index) => (
        <article className="mira-topic-detail-card" key={insight.id}>
          <span aria-hidden="true"><IconGlyph value={['↻', '♡', '◷', '♧'][index] || '✦'} /></span>
          <div>
            <h2>{insight.title}</h2>
            <p>{insight.content}</p>
            <small><IconGlyph value="▣" /> {formatDateLabel(insight.created_at)} · from a related conversation</small>
          </div>
          <span className="mira-row-chevron" aria-hidden="true"><IconGlyph value="›" /></span>
        </article>
      ))}
      <a className="mira-topic-saved-link" href={hrefForRoute('memory', {}, { tab: 'insights' })}><IconGlyph value="▱" /> View all insights</a>
    </div>
  );
}

function TopicNextStepsContent(props: { onContinue: () => void }) {
  return (
    <>
      <div className="mira-topic-card-list">
        {previewTopicStepCards.map(([icon, title, body, note]) => (
          <article className="mira-topic-detail-card mira-topic-step-card" key={title}>
            <span aria-hidden="true"><IconGlyph value={icon} /></span>
            <div>
              <h2>{title}</h2>
              <p>{body}</p>
              <small><IconGlyph value="◷" /> {note}</small>
            </div>
          </article>
        ))}
      </div>
      <section className="mira-topic-footer-card">
        <div>
          <h2>Want to go deeper?</h2>
          <p>Ask Mira to expand on any of these ideas or help you plan your next conversation.</p>
        </div>
        <button onClick={props.onContinue} type="button"><IconGlyph value="✦" /> Continue conversation</button>
      </section>
    </>
  );
}

function TopicHistoryContent(props: { onContinue: () => void }) {
  return (
    <>
      <section className="mira-topic-history">
        <header>
          <span aria-hidden="true"><IconGlyph value="◷" /></span>
          <div>
            <h2>Topic History</h2>
            <p>A timeline of your conversations and reflections.</p>
          </div>
        </header>
        <div className="mira-topic-history-list">
          {previewTopicHistoryRows.map(([date, time, body, kind]) => (
            <a className="mira-topic-history-row" href={hrefForRoute('chat', { conversationId: 'new' }, { source: 'topic-detail' })} key={`${date}-${time}`}>
              <time><strong>{date}</strong><span>{time}</span></time>
              <div>
                <p>{body}</p>
                <small>{kind}</small>
              </div>
              <span className="mira-row-chevron" aria-hidden="true"><IconGlyph value="›" /></span>
            </a>
          ))}
        </div>
      </section>
      <section className="mira-topic-footer-card">
        <span aria-hidden="true"><IconGlyph value="⌁" /></span>
        <div>
          <h2>Continue reflection</h2>
          <p>Pick up where you left off or explore what is emerging now.</p>
        </div>
        <button aria-label="Continue reflection" onClick={props.onContinue} type="button"><IconGlyph value="→" /></button>
      </section>
    </>
  );
}
